import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Avatar,
  Slide,
  InputAdornment,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { Close, Email, Phone, Key, Schedule, InfoOutlined } from '@mui/icons-material';
import { useRequestUpdatePhoneOrEmailMutation, useVerifyUpdatePhoneOrEmailMutation } from '../../../../components/services/services';
import { enqueueSnackbar } from 'notistack';
import TurkishMobilePhoneInput, {
  digitsOnly,
  formatTurkishMobileDisplay,
  toApiPhone90,
} from '../../../../components/TurkishMobilePhoneInput';

/**
 * API/listeden gelen mevcut cep numarasını 10 haneye indirir.
 * +905..., +90 5..., 905..., 05..., 5... (+, boşluk, tire otomatik temizlenir) desteklenir.
 * Sonuç her zaman 5 ile başlayan en fazla 10 hane (5xxxxxxxxx).
 */
function normalizeRawPhoneTo10Digits(raw: string): string {
  const d = digitsOnly(raw);
  if (!d.length) return '';
  let digits = d;
  if (digits.startsWith('90') && digits.length >= 2) digits = digits.slice(2);
  else if (digits.startsWith('0') && digits.length >= 1) digits = digits.slice(1);
  return digits.slice(0, 10);
}

function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export type UpdatePhoneOrEmailType = 'email' | 'phone';

export interface UpdatePhoneOrEmailUser {
  customerId: number;
  email: string;
  phoneNumber: string;
}

interface UpdatePhoneOrEmailModalProps {
  open: boolean;
  onClose: () => void;
  type: UpdatePhoneOrEmailType;
  user: UpdatePhoneOrEmailUser | null;
  onSuccess: () => void;
}

const UpdatePhoneOrEmailModal: React.FC<UpdatePhoneOrEmailModalProps> = ({
  open,
  onClose,
  type,
  user,
  onSuccess,
}) => {
  const isEmail = type === 'email';
  const [step, setStep] = useState<'form' | 'otp'>('form');
  const [newValue, setNewValue] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpExpiredMessage, setOtpExpiredMessage] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [phoneOtpPhase, setPhoneOtpPhase] = useState<'email' | 'phone'>('email');

  const [requestUpdate, { isLoading: requestLoading }] = useRequestUpdatePhoneOrEmailMutation();
  const [verifyUpdate, { isLoading: verifyLoading }] = useVerifyUpdatePhoneOrEmailMutation();

  const currentValue = user ? (isEmail ? user.email : user.phoneNumber ?? '') : '';
  const currentValueDisplay = isEmail ? currentValue : formatTurkishMobileDisplay(normalizeRawPhoneTo10Digits(currentValue));
  const hasCurrentValue = isEmail
    ? !!(user?.email?.trim())
    : /^5\d{9}$/.test(normalizeRawPhoneTo10Digits(user?.phoneNumber ?? ''));
  const label = isEmail ? 'E-posta' : 'Cep numarası';
  const title = isEmail ? 'E-posta Güncelle' : 'Cep No Güncelle';

  useEffect(() => {
    if (!open) {
      setStep('form');
      setNewValue('');
      setOtpCode('');
      setOtpExpiredMessage(false);
      setRemainingSeconds(0);
      setPhoneOtpPhase('email');
    }
  }, [open]);

  useEffect(() => {
    if (step !== 'otp') return;
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [step]);

  const handleContinue = async () => {
    if (!user) return;
    if (isEmail) {
      if (!newValue.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue.trim())) {
        enqueueSnackbar('Geçerli bir e-posta adresi girin.', { variant: 'error' });
        return;
      }
    } else {
      const digits = digitsOnly(newValue);
      if (!/^5\d{9}$/.test(digits)) {
        enqueueSnackbar('Geçerli bir cep numarası girin (5 ile başlayan 10 hane).', { variant: 'error' });
        return;
      }
    }

    try {
      const payloadValue = isEmail ? newValue.trim() : toApiPhone90(newValue);
      if (!isEmail && !payloadValue) {
        enqueueSnackbar('Geçerli bir cep numarası girin (5 ile başlayan 10 hane).', { variant: 'error' });
        return;
      }
      const result = await requestUpdate({
        NewValue: payloadValue,
        IsEmail: isEmail,
        CustomerId: user.customerId,
      }).unwrap();
      if (result?.success !== false) {
        const expireAt = result?.data?.expireAtSeconds ?? 180;
        setRemainingSeconds(expireAt);
        if (!isEmail) {
          setPhoneOtpPhase('email');
        }
        setStep('otp');
        setOtpExpiredMessage(false);
      }
    } catch (err: any) {
      if (err?.status === 419) {
        setOtpExpiredMessage(true);
        enqueueSnackbar('Kodun süresi doldu, tekrar kod isteyin.', { variant: 'warning' });
      }
      // dataSlice may already show error for other statuses
    }
  };

  const handleVerify = async () => {
    if (!user || !otpCode.trim()) {
      enqueueSnackbar('Doğrulama kodunu girin.', { variant: 'error' });
      return;
    }
    try {
      const result = await verifyUpdate({
        Code: otpCode.trim(),
        CustomerId: user.customerId,
      }).unwrap();
      if (result?.success !== false) {
        if (!isEmail && phoneOtpPhase === 'email') {
          // İlk başarılı doğrulama: e-posta OTP doğrulandı, şimdi telefon OTP aşamasına geç
          setPhoneOtpPhase('phone');
          setOtpCode('');
          setOtpExpiredMessage(false);
          setRemainingSeconds(180);
          enqueueSnackbar('Yeni telefon numaranıza doğrulama kodu gönderildi.', { variant: 'info' });
        } else {
          enqueueSnackbar(isEmail ? 'E-posta güncellendi.' : 'Cep numarası güncellendi.', { variant: 'success' });
          onSuccess();
          onClose();
        }
      }
    } catch (err: any) {
      if (err?.status === 419) {
        setOtpExpiredMessage(true);
        enqueueSnackbar('Kodun süresi doldu, tekrar kod isteyin.', { variant: 'warning' });
      }
    }
  };

  const handleResendOtp = async () => {
    if (!user) return;
    const payloadValue = isEmail ? newValue.trim() : toApiPhone90(newValue);
    if (!isEmail && !payloadValue) {
      enqueueSnackbar('Geçerli bir cep numarası girin (5 ile başlayan 10 hane).', { variant: 'error' });
      return;
    }
    const isEmailRequest = isEmail;

    setOtpExpiredMessage(false);
    try {
      const result = await requestUpdate({
        NewValue: payloadValue,
        IsEmail: isEmailRequest,
        CustomerId: user.customerId,
      }).unwrap();
      if (result?.success !== false) {
        const expireAt = result?.data?.expireAtSeconds ?? 180;
        setRemainingSeconds(expireAt);
        setOtpCode('');
        enqueueSnackbar('Yeni doğrulama kodu gönderildi.', { variant: 'info' });
      }
    } catch (err: any) {
      if (err?.status === 419) {
        setOtpExpiredMessage(true);
        enqueueSnackbar('Kodun süresi doldu, tekrar kod isteyin.', { variant: 'warning' });
      }
    }
  };

  const isLoading = requestLoading || verifyLoading;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isValidNewValue = isEmail
    ? Boolean(newValue.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue.trim()))
    : /^5\d{9}$/.test(newValue);

  const headerGradient = isEmail
    ? 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
    : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)';
  const buttonColor = isEmail ? '#6366f1' : '#0ea5e9';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'down' } as any}
      fullScreen={false}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: isMobile ? '100%' : '500px',
          maxHeight: '90vh',
          zIndex: 99999999,
          backgroundColor: theme.palette.grey[50],
          borderRadius: isMobile ? 1 : '4px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header - kart listesi modalları ile aynı stil */}
      <Box
        sx={{
          background: headerGradient,
          padding: { xs: '12px 16px', sm: '16px 20px' },
          borderRadius: '4px 4px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            zIndex: 0,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Avatar sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', width: 32, height: 32 }}>
            {isEmail ? <Email fontSize="small" /> : <Phone fontSize="small" />}
          </Avatar>
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: '1rem !important',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}
          >
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            position: 'relative',
            zIndex: 1,
            width: 36,
            height: 36,
            border: '1px solid',
            borderColor: 'rgba(255,255,255,0.3)',
            backgroundColor: 'rgba(255,255,255,0.1)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderColor: 'rgba(255,255,255,0.5)',
              transform: 'rotate(90deg)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Close sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ m: 0, px: { xs: 2, sm: 3 }, pt: 3, pb: 2, '&.MuiDialogContent-root': { m: 0 } }}>
        {step === 'form' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {hasCurrentValue && (
              <TextField
                label={`Mevcut ${label}`}
                value={currentValueDisplay}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              />
            )}
            {isEmail ? (
              <TextField
                label={`Yeni ${label}`}
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                fullWidth
                size="small"
                type="email"
                placeholder="ornek@firma.com"
                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              />
            ) : (
              <TurkishMobilePhoneInput
                fullWidth
                label={`Yeni ${label}`}
                placeholder="+90 5"
                valueDigits={newValue}
                onChangeDigits={(digits10) => setNewValue(digits10)}
                emptyDisplayValue="+90 5"
                sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
              />
            )}
          </Box>
        )}
        {step === 'otp' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {otpExpiredMessage && (
              <Typography variant="body1" color="warning.main" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                Kodun süresi doldu, tekrar kod isteyin.
              </Typography>
            )}
            {(isEmail || !isEmail) && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  bgcolor: '#FFF8E1',
                  border: '1px solid',
                  borderColor: '#FFE082',
                  mb: 1,
                }}
              >
                <InfoOutlined sx={{ fontSize: 18, color: '#8D6E00', mt: '2px' }} />
                <Typography variant="body2" sx={{ fontSize: '0.95rem', color: '#8D6E00', lineHeight: 1.4 }}>
                  {isEmail
                    ? 'Yeni e-posta adresine doğrulama kodu gönderildi. Kodu girip onaylamanız gerekmektedir.'
                    : phoneOtpPhase === 'email'
                    ? 'E-posta adresine doğrulama kodu gönderildi. Kodu girip onaylamanız gerekmektedir.'
                    : 'Yeni telefon numarasına doğrulama kodu gönderildi. Kodu girip onaylamanız gerekmektedir.'}
                </Typography>
              </Box>
            )}
            <TextField
              label="Doğrulama kodu"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              fullWidth
              size="small"
              inputProps={{ maxLength: 6, inputMode: 'numeric' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Key fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderRadius: 2,
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Schedule sx={{ fontSize: 28, color: 'primary.main' }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.2 }}
                  >
                    Kodun geçerlilik süresi
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      letterSpacing: '0.05em',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {remainingSeconds > 0 ? formatTimer(remainingSeconds) : 'Süresi doldu'}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="text"
                size="small"
                onClick={handleResendOtp}
                disabled={requestLoading || remainingSeconds > 0}
                sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}
              >
                Tekrar kod gönder
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: '#0b6bcb !important',
            border: '1px solid #0b6bcb',
            fontSize: '0.875rem',
            height: '30px',
            padding: '4px 16px',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          İptal
        </Button>
        {step === 'form' ? (
          <Button
            onClick={handleContinue}
            disabled={isLoading || !isValidNewValue}
            sx={{
              color: 'white !important',
              height: '30px',
              minWidth: '120px',
              padding: '4px 16px !important',
              fontSize: '0.875rem',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              background: buttonColor,
              '&:hover': { background: buttonColor, filter: 'brightness(0.9)' },
              '&.Mui-disabled': { background: `${buttonColor}80`, color: 'white !important' },
            }}
          >
            {!isLoading ? 'Devam Et' : <CircularProgress sx={{ width: '20px !important', height: '20px !important', color: 'white' }} />}
          </Button>
        ) : (
          <Button
            onClick={handleVerify}
            disabled={isLoading || otpCode.length !== 6}
            sx={{
              color: 'white !important',
              height: '30px',
              minWidth: '120px',
              padding: '4px 16px !important',
              fontSize: '0.875rem',
              alignItems: 'center',
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              background: buttonColor,
              '&:hover': { background: buttonColor, filter: 'brightness(0.9)' },
              '&.Mui-disabled': { background: `${buttonColor}80`, color: 'white !important' },
            }}
          >
            {!isLoading ? 'Doğrula' : <CircularProgress sx={{ width: '20px !important', height: '20px !important', color: 'white' }} />}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UpdatePhoneOrEmailModal;
